import { addDays, startOfWeek } from './dateRangePresets';

/**
 * A one-click day offered as a chip under `NxDateInput` / `NxDateTimeInput`.
 *
 * The label is plain text. A chip that names a concrete day („Fr 10.07.“) is
 * built by the caller from the same reference day it passes as `today`.
 */
export type NxDatePreset = {
  id: string;
  label: string;
  /** The day this preset stands for, relative to `today` (ISO `YYYY-MM-DD`). `null` clears the field. */
  date: (today: string) => string | null;
  /**
   * `NxDateTimeInput` only: the time (`HH:mm`) the preset sets along with the
   * day. Without it the time already in the field stays.
   */
  time?: string;
};

export type NxDatePresetLabels = {
  today: string;
  tomorrow: string;
  nextWeek: string;
  none: string;
};

const defaultLabels: NxDatePresetLabels = {
  today: 'Heute',
  tomorrow: 'Morgen',
  nextWeek: 'Nächste Woche',
  none: 'Ohne',
};

/** Monday of the week after the one `today` lies in. */
export function nextWeekStart(today: string): string {
  return addDays(startOfWeek(today), 7);
}

/**
 * The quick picks of the work-organisation forms: Heute / Morgen / Nächste
 * Woche (its Monday) / Ohne (clears the field). Pass other labels for another
 * language; build an own list for other days or for presets with a time.
 */
export function defaultDatePresets(labels: Partial<NxDatePresetLabels> = {}): NxDatePreset[] {
  const l = { ...defaultLabels, ...labels };
  return [
    { id: 'today', label: l.today, date: (today) => today },
    { id: 'tomorrow', label: l.tomorrow, date: (today) => addDays(today, 1) },
    { id: 'next-week', label: l.nextWeek, date: nextWeekStart },
    { id: 'none', label: l.none, date: () => null },
  ];
}

/** Milliseconds of the UTC midnight of an ISO day — calendar arithmetic free of DST shifts. */
function utcDay(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

const DAY_MS = 86_400_000;

/** Whole days from `from` to `to`; negative when `to` lies before `from`. */
export function daysBetween(from: string, to: string): number {
  return Math.round((utcDay(to) - utcDay(from)) / DAY_MS);
}

/** Saturday or Sunday. */
export function isWeekend(iso: string): boolean {
  const weekday = new Date(utcDay(iso)).getUTCDay();
  return weekday === 0 || weekday === 6;
}

/** Neither a weekend day nor one of `holidays` (ISO day → name, supplied by the caller). */
export function isWorkingDay(iso: string, holidays?: Readonly<Record<string, string>>): boolean {
  return !isWeekend(iso) && !holidays?.[iso];
}

/**
 * The first working day after `iso`. `null` when there is none within a year,
 * which only a holiday list covering every weekday can cause.
 */
export function nextWorkingDay(
  iso: string,
  holidays?: Readonly<Record<string, string>>
): string | null {
  let day = iso;
  for (let i = 0; i < 366; i++) {
    day = addDays(day, 1);
    if (isWorkingDay(day, holidays)) return day;
  }
  return null;
}

/**
 * Why `iso` is no working day: the holiday's name, `weekendLabel` for a
 * Saturday or Sunday, `null` for a working day. A holiday on a weekend is
 * reported by its name — it says more than „Wochenende“.
 */
export function nonWorkingDayReason(
  iso: string,
  holidays: Readonly<Record<string, string>> | undefined,
  weekendLabel: string
): string | null {
  const holiday = holidays?.[iso];
  if (holiday) return holiday;
  return isWeekend(iso) ? weekendLabel : null;
}

/**
 * Distance of a day to today in words, in `locale`: „heute“, „morgen“,
 * „gestern“ for the neighbours, „in 3 Tagen“ / „vor 3 Tagen“ beyond them.
 * (`numeric: 'auto'` throughout would also turn ±2 into „übermorgen“ /
 * „vorgestern“.)
 */
export function relativeDayLabel(days: number, locale: string): string {
  const numeric = Math.abs(days) <= 1 ? 'auto' : 'always';
  return new Intl.RelativeTimeFormat(locale, { numeric }).format(days, 'day');
}
